import { useState } from 'react';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { StatusHorario } from '../../Enum';
import Dropdown from '../../../../components/Dropdown';
import { HorariosAtendenteService } from '../../Service';
import { useAppContext } from '../../../../contexts/AppContext';
import { usePlanAccess } from '../../../../plans/usePlanAccess';
import { SALONFLOW_FEATURES } from '../../../../plans/salonflowPlans';
import PaymentMethodModal from './PaymentMethodModal';

const horarioService = new HorariosAtendenteService();

interface ScheduleStatusProps {
    id: string;
    clientId: string;
    status: StatusHorario;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    amount?: number;
    paymentMethod?: string;
    paymentStatus?: string;
    paymentProvider?: string;
}

const statusButtonClasses: Record<StatusHorario, string> = {
    [StatusHorario.PENDENTE]:
        'rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700 transition-colors hover:border-amber-300 hover:bg-amber-100 hover:text-amber-800',
    [StatusHorario.AGENDADO]:
        'rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-sky-700 transition-colors hover:border-sky-300 hover:bg-sky-100 hover:text-sky-800',
    [StatusHorario.CONCLUIDO]:
        'rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700 transition-colors hover:border-emerald-300 hover:bg-emerald-100 hover:text-emerald-800',
    [StatusHorario.CANCELADO]:
        'rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 transition-colors hover:border-rose-300 hover:bg-rose-100 hover:text-rose-800',
};

export default function ScheduleStatus({
    id,
    clientId,
    status,
    size = 'sm',
    amount = 0,
    paymentMethod,
    paymentStatus,
    paymentProvider,
}: ScheduleStatusProps) {
    const { user } = useAuthContext();
    const { errorSnackbar, successSnackbar } = useAppContext();
    const { hasFeature } = usePlanAccess();
    const [loading, setLoading] = useState(false);
    const [paymentModal, setPaymentModal] = useState<'conclude' | 'register' | 'change' | null>(null);
    const [savedMethod, setSavedMethod] = useState<string>();

    const isClient = user?.uid === clientId;
    const cursorStyle = isClient ? 'default' : 'pointer';
    const currentMethod = savedMethod ?? paymentMethod;
    const hasFinance = hasFeature(SALONFLOW_FEATURES.FINANCE);
    const needsPayment = hasFinance && amount > 0 && !currentMethod && paymentStatus !== 'PENDING';
    const canChangePayment = hasFinance && !!currentMethod && paymentStatus !== 'PENDING' && paymentProvider !== 'ASAAS';

    const conclude = async (method?: string) => {
        try {
            setLoading(true);
            await horarioService.conclude(id, method);
            setPaymentModal(null);
        } catch (e: any) {
            errorSnackbar?.(e?.response?.data?.message || 'Não foi possível concluir o atendimento.');
        } finally {
            setLoading(false);
        }
    };

    const registerPayment = async (method: string) => {
        try {
            setLoading(true);
            await horarioService.registerPayment(id, method);
            setSavedMethod(method);
            setPaymentModal(null);
            successSnackbar?.('Pagamento registrado.');
        } catch (e: any) {
            errorSnackbar?.(e?.response?.data?.message || 'Não foi possível registrar o pagamento.');
        } finally {
            setLoading(false);
        }
    };

    const changePayment = async (method: string) => {
        try {
            setLoading(true);
            await horarioService.changePayment(id, method);
            setSavedMethod(method);
            setPaymentModal(null);
            successSnackbar?.('Forma de pagamento alterada.');
        } catch (e: any) {
            errorSnackbar?.(e?.response?.data?.message || 'Não foi possível alterar a forma de pagamento.');
        } finally {
            setLoading(false);
        }
    };

    switch (status) {
        case StatusHorario.PENDENTE:
            return (
                <Dropdown
                    loading={loading}
                    unstyled
                    actions={
                        isClient
                            ? []
                            : [
                                  {
                                      label: 'Aprovar',
                                      action: async () => {
                                          try {
                                              setLoading(true);
                                              await horarioService.approve(id);
                                          } catch (e) {
                                              console.log(e);
                                          } finally {
                                              setLoading(false);
                                          }
                                      },
                                  },
                                  {
                                      label: 'Cancelar',
                                      action: async () => {
                                          try {
                                              setLoading(true);
                                              await horarioService.cancel(id);
                                              window.dispatchEvent(new CustomEvent('waitlist:nudge'));
                                          } catch (e) {
                                              console.log(e);
                                          } finally {
                                              setLoading(false);
                                          }
                                      },
                                  },
                              ]
                    }
                    label="Pendente"
                    buttonClass={`btn btn-${size} min-h-0 ${statusButtonClasses[StatusHorario.PENDENTE]}`}
                />
            );
        case StatusHorario.AGENDADO:
            return (
                <>
                    <Dropdown
                        loading={loading}
                        unstyled
                        label="Agendado"
                        buttonClass={`btn btn-${size} min-h-0 ${statusButtonClasses[StatusHorario.AGENDADO]}`}
                        actions={
                            isClient
                                ? []
                                : [
                                      {
                                          label: 'Concluir',
                                          action: async () => {
                                              if (needsPayment) {
                                                  setPaymentModal('conclude');
                                                  return;
                                              }
                                              await conclude();
                                          },
                                      },
                                      {
                                          label: 'Cancelar',
                                          action: async () => {
                                              try {
                                                  setLoading(true);
                                                  await horarioService.cancel(id);
                                                  window.dispatchEvent(new CustomEvent('waitlist:nudge'));
                                              } catch (e) {
                                                  console.log(e);
                                              } finally {
                                                  setLoading(false);
                                              }
                                          },
                                      },
                                  ]
                        }
                    />
                    {paymentModal === 'conclude' && (
                        <PaymentMethodModal
                            title="Concluir atendimento"
                            confirmLabel="Concluir"
                            onClose={() => setPaymentModal(null)}
                            onConfirm={conclude}
                        />
                    )}
                </>
            );
        case StatusHorario.CONCLUIDO:
            return (
                <>
                    <Dropdown
                        loading={loading}
                        unstyled
                        actions={isClient
                            ? []
                            : [
                                ...(needsPayment
                                    ? [
                                          {
                                              label: 'Registrar pagamento',
                                              action: async () => setPaymentModal('register'),
                                          },
                                      ]
                                    : []),
                                ...(canChangePayment
                                    ? [
                                          {
                                              label: 'Alterar forma de pagamento',
                                              action: async () => setPaymentModal('change'),
                                          },
                                      ]
                                    : []),
                                {
                                    label: 'Cancelar',
                                    action: async () => {
                                        try {
                                            setLoading(true);
                                            await horarioService.cancel(id);
                                            window.dispatchEvent(new CustomEvent('waitlist:nudge'));
                                        } catch (e) {
                                            console.log(e);
                                        } finally {
                                            setLoading(false);
                                        }
                                    },
                                },
                            ]}
                        label="Concluído"
                        buttonClass={`btn btn-${size} min-h-0 ${statusButtonClasses[StatusHorario.CONCLUIDO]}`}
                    />
                    {paymentModal === 'register' && (
                        <PaymentMethodModal
                            title="Registrar pagamento"
                            confirmLabel="Registrar pagamento"
                            onClose={() => setPaymentModal(null)}
                            onConfirm={registerPayment}
                        />
                    )}
                    {paymentModal === 'change' && (
                        <PaymentMethodModal
                            title="Alterar forma de pagamento"
                            confirmLabel="Salvar"
                            currentMethod={currentMethod}
                            onClose={() => setPaymentModal(null)}
                            onConfirm={changePayment}
                        />
                    )}
                </>
            );
        case StatusHorario.CANCELADO:
            return (
                <Dropdown
                    loading={loading}
                    unstyled
                    actions={[]}
                    label="Cancelado"
                    buttonClass={`btn btn-${size} min-h-0 ${statusButtonClasses[StatusHorario.CANCELADO]}`}
                />
            );
        default:
            return (
                <button
                    type="button"
                    style={{ cursor: cursorStyle, minWidth: 'fit-content' }}
                    className={`btn opacity-80 btn-${size}`}
                >
                    btn-secondary
                </button>
            );
    }
}
